import React, { useState, useEffect, useMemo, useRef } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryByAreaByProductByStatus } from "../../../graphql/inventory/custom-queries";
import { canMergeInventoryItems } from "../../../utils/damagedInventory";
import { getOutboundShipment } from "../../../graphql/outbound/custom-queries";
import {
  createInventoryItem,
  updateInventorieInbound,
  updateInventoryItemMini,
} from "../../../graphql/inventory/custom-mutations";
import {
  performUpdateInventoryMini,
  sanitizeInventoryItem,
} from "../../../graphql/inventory/inventoryCRUD";
import {
  createShipmentLineItemInventoryItem,
  createShipmentLineItem,
  updateShipmentLineItem,
} from "../../../graphql/mutations";
import { getTrackCar } from "../../../graphql/queries";

import { useNotify, useRedirect } from "react-admin";

import {
  Box,
  Button,
  CardActions,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Backdrop,
  CardContent,
} from "@material-ui/core";

import HighlightOffIcon from "@material-ui/icons/HighlightOff";

import { updateOutboundQuantity } from "../../../managerApp/resources/outboundShipments/lineItem/PickListItemUtils";
import { performUpdateInboundMini } from "../../../graphql/inbound/inboundCRUD";
import { logError } from "../../../utils/appUtils";
import { useTrackCarSubscriptionRefresh } from "../../../utils/useTrackCarSubscriptionRefresh";
import {
  AreaType,
  CarrierType,
  InventoryStatus,
  NoteType,
  WorkOrderState,
  WorkOrderSteps,
} from "../../../utils/constants";
import { useWorkOrderContext } from "./context";
import ErrorCard from "../../../components/ErrorCard";
import {
  ensureBulkMobilePlacementAndAggregateOnInboundComplete,
  getISOtainerInventoryItems,
  getLocationFullName,
  isBulkArea,
  isTrack,
  isTruckToRail,
  isUnmanagedLocation,
} from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { getLogState } from "../../../utils/utils";
import CrewCard from "../../../components/common/CrewCard";
import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import { calcNetWeight } from "../../../managerApp/resources/outboundShipments/OutboundUtils";
import { ChecklistDialog } from "../../../components/common/ChecklistDialog";
import { SealDialog } from "./Seals/SealsDialog";
import { emailPOD } from "../../../managerApp/resources/inboundShipments/InboundUtils";
import { buildAutoCreatedShipmentLineItem } from "./outboundAssociationUtils";
import { InboundInventoryList } from "./InboundInventoryList";
import { ExceptionDialog } from "./ExceptionDialog";
import { SealsConfirmationDialog } from "./SealsConfirmationDialog";
import { TrainIcon, TruckIcon } from "../../../components/common/iconography";
import { InboundStepperProgress } from "./InboundStepperProgress";
import { ContainerNumberDialog } from "../../../components/common/ContainerNumberDialog";
import { RailCarNumberVerificationDialog } from "../../../components/common/RailCarNumberVerificationDialog";
import { SpeedDialList } from "../../../components/common/SpeedDialList";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";
import { getInboundSpeedDialActions } from "./placedShipmentActions";
import {
  getAcceptSealDialogState,
  shouldBlockAcceptForContainerVerification,
} from "./placedShipmentValidation";
import {
  getArrivingRailcarVerificationResetKey,
  getArrivingRailcarVerificationState,
  shouldBlockAcceptForRailcarVerification,
} from "../../../utils/railCarUtils";

const getByProductNameKey = (inventory) =>
  `${inventory.areaId}-${inventory.zoneId}-${inventory.productName}-${inventory.inventoryUOM}`;
const getByProductIdKey = (inventory) =>
  `${inventory.areaId}-${inventory.zoneId}-${inventory.productId}-${inventory.inventoryUOM}`;
const QUANTITY_PLACEHOLDER = "QUANTITY_PLACEHOLDER";
const isReviewState = (state) => state === WorkOrderState.PENDING_REVIEW;

export const PlacedShipmentCard = ({ classes, match, history }) => {
  const { state, dispatch, getWorkOrder, saveWorkOrder } =
    useWorkOrderContext();
  const {
    currentShipment = null,
    notes = {},
    loading = true,
    isContainerNumberValidated = false,
  } = state || {};
  const [completeFlag, setCompleteFlag] = useState(false);
  const [equipmentVerification, setEquipmentVerification] = useState(null);
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState(false);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  const [isSealDialogOpen, setIsSealDialogOpen] = useState(false);
  const [isSealsConfirmationOpen, setIsSealsConfirmationOpen] = useState(false);
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false);
  const [isRailcarValidated, setIsRailcarValidated] = useState(false);
  const [
    pendingAcceptAfterRailcarValidation,
    setPendingAcceptAfterRailcarValidation,
  ] = useState(false);
  const [
    pendingAcceptAfterContainerValidation,
    setPendingAcceptAfterContainerValidation,
  ] = useState(false);
  const [
    cloneableInventoriesByProductName,
    setCloneableInventoriesByProductName,
  ] = useState({});
  const [cloneableInventoriesByProductId, setCloneableInventoriesByProductId] =
    useState({});
  const [noteErrorMsg, setNoteErrorMsg] = useState("");
  const [invalidIdError, setInvalidIdError] = useState();
  const handleSaveRef = useRef(null);
  const notify = useNotify();
  const redirect = useRedirect();
  const note = currentShipment ? notes[currentShipment.id] : "";
  const railcarVerificationState = getArrivingRailcarVerificationState({
    currentShipment,
    isRailcarValidated,
  });
  const sourceInboundShipmentId =
    currentShipment?.inboundShipmentId || currentShipment?.id;
  const railcarVerificationResetKey = getArrivingRailcarVerificationResetKey({
    currentShipment,
  });

  const handleEquipmentVerificationClose = () => {
    setEquipmentVerification(null);
    setPendingAcceptAfterRailcarValidation(false);
    setPendingAcceptAfterContainerValidation(false);
  };

  const handleEquipmentVerificationSuccess = () => {
    if (!equipmentVerification) return;
    const { kind, resumeSave } = equipmentVerification;
    if (kind === "railcar") {
      setIsRailcarValidated(true);
      if (resumeSave) setPendingAcceptAfterRailcarValidation(true);
      setEquipmentVerification(null);
      return;
    }
    if (kind === "container") {
      dispatch({ type: "SET_IS_CONTAINER_NUMBER_VALIDATED", payload: true });
      if (resumeSave) setPendingAcceptAfterContainerValidation(true);
      setEquipmentVerification(null);
      return;
    }
    console.warn(
      "PlacedShipmentCard:handleEquipmentVerificationSuccess unknown kind",
      { kind },
    );
    setEquipmentVerification(null);
  };

  useEffect(() => {
    dispatch({ type: "SET_LOADING", payload: true });
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsRailcarValidated(false);
    setEquipmentVerification(null);
    setPendingAcceptAfterRailcarValidation(false);
    setPendingAcceptAfterContainerValidation(false);
  }, [railcarVerificationResetKey]);

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(currentShipment?.reloadYardId),
    label: "PlacedShipmentCard",
    notify,
    shouldRefresh: (trackCar) => {
      if (!trackCar?.reloadYardId) return false;
      if (trackCar.reloadYardId !== currentShipment.reloadYardId) return false;
      if (
        sourceInboundShipmentId &&
        trackCar?.shipmentId &&
        trackCar.shipmentId !== sourceInboundShipmentId
      ) {
        return false;
      }
      return true;
    },
    onRefresh: (trackCar, eventName) => {
      if (
        eventName === "update" &&
        trackCar?.shipmentId === sourceInboundShipmentId &&
        trackCar?.trackId
      ) {
        dispatch({
          type: "UPDATE_SHIPMENT_LOCATION",
          payload: {
            shipmentId: trackCar.shipmentId,
            trackId: trackCar.trackId,
            locationFullName: trackCar.locationFullName,
          },
        });
      }
      getData({ showSpinner: false });
    },
  });

  const getData = async ({ showSpinner = true } = {}) => {
    try {
      if (showSpinner) {
        dispatch({ type: "SET_LOADING", payload: true });
      }

      const workOrder = await getWorkOrder(match);
      if (!workOrder) {
        setInvalidIdError(`Work order ${match.params.id} was not found.`);
        return;
      }

      await attachTrackCarMetadata(workOrder);
      processWorkOrder(workOrder);
    } catch (err) {
      logError("ProcessWorkOrderCard:getData", err, notify);
    } finally {
      if (showSpinner) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  };

  const attachTrackCarMetadata = async (workOrder) => {
    try {
      const trackCarId = workOrder?.inboundShipmentId || workOrder?.id;
      if (!trackCarId) {
        workOrder.trackCar = null;
        return;
      }
      const response = await API.graphql(
        graphqlOperation(getTrackCar, {
          id: trackCarId,
        }),
      );
      const trackCar = response?.data?.getTrackCar || null;
      workOrder.trackCar = trackCar;
    } catch (err) {
      logError("ProcessWorkOrderCard:attachTrackCarMetadata", err, notify);
      workOrder.trackCar = null;
    }
  };

  const processWorkOrder = (workOrder) => {
    if (workOrder.state === WorkOrderState.READY) {
      workOrder.stepName = WorkOrderSteps[0];
    } else if (workOrder.state === WorkOrderState.IN_PROGRESS) {
      workOrder.stepName = WorkOrderSteps[1];
    } else {
      workOrder.stepName = WorkOrderSteps[2];
    }
    dispatch({ type: "SET_CURRENT_SHIPPMENT", payload: workOrder });

    handleBulkOrTrackInventories(workOrder);
  };

  const handleBulkOrTrackInventories = (workOrder) => {
    if (
      workOrder.area.areaType !== AreaType.BULK &&
      workOrder.area.areaType !== AreaType.TRACK &&
      workOrder.inventories.items.length > 1
    ) {
      getCloneableInventories(workOrder);
    }
  };

  const actions = useMemo(() => {
    if (!currentShipment) return [];

    return getInboundSpeedDialActions({
      inboundShipment: currentShipment,
      isContainerNumberValidated,
      shipmentRailcarVerificationState: railcarVerificationState,
      onOpenExceptionDialog: () => setIsExceptionDialogOpen(true),
      onOpenChecklistDialog: () => setIsChecklistDialogOpen(true),
      onOpenSealDialog: () => setIsSealDialogOpen(true),
      onOpenContainerNumberDialog: () =>
        setEquipmentVerification({ kind: "container", resumeSave: false }),
      onOpenRailcarDialog: () =>
        setEquipmentVerification({ kind: "railcar", resumeSave: false }),
      onSealBlocked: () =>
        notify(
          "Validate the arriving railcar # before validating seals.",
          "warning",
        ),
      onSealBlockedByContainer: () =>
        notify("Validate container # before validating seals.", "warning"),
    });
  }, [
    currentShipment,
    isContainerNumberValidated,
    railcarVerificationState,
    notify,
  ]);

  const getCloneableInventories = (workOrder) => {
    const cloneableInventoriesByProductName = {};
    const cloneableInventoriesByProductId = {};
    workOrder.inventories.items.forEach((inventory) => {
      if (
        inventory.inventoryItems.items?.length > 0 &&
        inventory.status === InventoryStatus.UNLOADED &&
        !inventory.isDamaged &&
        !inventory.hasException &&
        inventory.inventoryItems.items.find((inventoryItem) =>
          isUnmanagedLocation(inventoryItem.location),
        )
      ) {
        const byProductNameKey = getByProductNameKey(inventory);
        const byProductIdKey = getByProductIdKey(inventory);
        if (!cloneableInventoriesByProductName[byProductNameKey])
          cloneableInventoriesByProductName[byProductNameKey] = {};
        if (!cloneableInventoriesByProductId[byProductIdKey])
          cloneableInventoriesByProductId[byProductIdKey] = {};
        inventory.inventoryItems.items.forEach((inventoryItem) => {
          if (
            isUnmanagedLocation(inventoryItem.location) &&
            !inventoryItem.hasException
          ) {
            const cloneableInventoryItem = {
              ...inventoryItem,
              name: `Place ${QUANTITY_PLACEHOLDER} ${getUnitName(
                inventory.inventoryUOM,
              )} in ${inventoryItem.locationFullName}`,
            };
            cloneableInventoriesByProductName[byProductNameKey][
              inventoryItem.locationId
            ] = cloneableInventoryItem;
            cloneableInventoriesByProductId[byProductIdKey][
              inventoryItem.locationId
            ] = cloneableInventoryItem;
          }
        });
      }
    });
    setCloneableInventoriesByProductName(cloneableInventoriesByProductName);
    setCloneableInventoriesByProductId(cloneableInventoriesByProductId);
  };

  const backHandler = () => {
    dispatch({
      type: "CLEAR_NOTE",
      payload: currentShipment,
    });
    history.push("/processWorkOrders");
  };

  const getSteps = () => {
    return currentShipment.secondaryReview
      ? [...WorkOrderSteps]
      : WorkOrderSteps.slice(0, 2);
  };

  const getActiveStep = () => {
    if (currentShipment.state === WorkOrderState.READY) return 0;
    if (currentShipment.state === WorkOrderState.IN_PROGRESS) return 1;
    return currentShipment.secondaryReview ? 2 : 1;
  };

  const isStepCompleted = (index) => {
    return index < getActiveStep();
  };

  const isStepFailed = (index) => {
    return isReviewState(currentShipment?.state) && index === getActiveStep();
  };

  const saveInventory = (inventory) => {
    const toUpdateInventory = {
      ...inventory,
      aggregateInventoryId: inventory.aggregateInventoryId ?? "",
    };
    toUpdateInventory.quantityReceived = inventory.quantityReceived ?? null;
    if (currentShipment.state === WorkOrderState.COMPLETED) {
      toUpdateInventory.quantityOnHand = inventory.quantityReceived;
      toUpdateInventory.quantityPhysical = inventory.quantityReceived;
      if (isTruckToRail(inventory.areaId, currentShipment)) {
        // Change quantityCommitted to the sum of quantities in rail cars (some can be in a location)
        toUpdateInventory.quantityCommitted =
          toUpdateInventory.quantityReceived;
        const newHistory = {
          type: NoteType.SHIPMENT,
          date: new Date(),
          by: window.appUser?.fullName,
          text: `Committed Outbound Qty. of ${inventory.quantityReceived}`,
        };
        if (toUpdateInventory.history)
          toUpdateInventory.history.push(newHistory);
        else toUpdateInventory.history = [newHistory];
      }
    }
    toUpdateInventory.updatedAt = new Date().toISOString();
    // Remove unnecessary fields
    [
      "account",
      "inboundShipment",
      "location",
      "product",
      "reloadYardCommodity",
      "shipmentLineItems",
      "inventoryItems",
      "area",
    ].forEach((key) => delete toUpdateInventory[key]);
    return toUpdateInventory;
  };

  const handleClone = async (inventory, inventoryItem) => {
    if (!inventory || !inventoryItem) return;
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const toCloneInventoryItem = { ...inventoryItem };
      delete toCloneInventoryItem.id;
      delete toCloneInventoryItem.name;
      toCloneInventoryItem.inventoryId = inventory.id;
      toCloneInventoryItem.groups = window.appUser.reloadYard.groups;
      toCloneInventoryItem.quantityOnHand = inventory.quantityAdvised;
      toCloneInventoryItem.accountId = currentShipment.accountId;
      toCloneInventoryItem.inboundShipmentId = sourceInboundShipmentId;
      toCloneInventoryItem.reloadYardId = window.appUser.reloadYard.id;
      toCloneInventoryItem.unloadedBy = window.appUser.fullName;
      toCloneInventoryItem.unloadedAt = new Date();
      toCloneInventoryItem.locationFullName = inventoryItem.location
        ? getLocationFullName(inventoryItem.location)
        : inventoryItem.locationFullName || "";
      const historyText = `Placed ${
        toCloneInventoryItem.quantityOnHand
      } ${getUnitName(inventory.inventoryUOM)} in ${toCloneInventoryItem.locationFullName}`;
      const itemHistory = {
        type: NoteType.SHIPMENT,
        date: new Date(),
        by: window.appUser?.fullName,
        text: historyText,
      };
      toCloneInventoryItem.history = [itemHistory];
      delete toCloneInventoryItem.location;
      debugger;
      const promiseList = [];
      promiseList.push(
        API.graphql(
          graphqlOperation(createInventoryItem, {
            input: toCloneInventoryItem,
          }),
        ),
      );

      const toCloneInventory = currentShipment.inventories.items.find(
        (itm) => itm.id === inventoryItem.inventoryId,
      );
      const toUpdateInventory = {};
      toUpdateInventory.id = inventory.id;
      toUpdateInventory.status = toCloneInventory.status;
      toUpdateInventory.productId = toCloneInventory.productId || "";
      toUpdateInventory.lotNumber = inventory.lotNumber;
      toUpdateInventory.accountName =
        inventory.accountName || currentShipment.accountName;
      toUpdateInventory.productName = inventory.productName;
      toUpdateInventory.inboundShipmentId = inventory.inboundShipmentId;
      toUpdateInventory.reloadYardId = window.appUser.reloadYard.id;
      toUpdateInventory.accountId = currentShipment.accountId;
      toUpdateInventory.inboundShipmentId =
        inventory.inboundShipmentId || sourceInboundShipmentId;
      toUpdateInventory.quantityReceived = inventory.quantityAdvised;
      toUpdateInventory.receivedAt = currentShipment.placedDateTime
        ? currentShipment.placedDateTime
        : new Date();
      if (!toUpdateInventory.history) toUpdateInventory.history = [];
      const newHistory = {
        type: NoteType.SHIPMENT,
        date: new Date(),
        by: window.appUser.fullName,
        text: `Inventory saved | status: ${toUpdateInventory.status} | quantity received: ${toUpdateInventory.quantityReceived}`,
      };
      if (toUpdateInventory.history) toUpdateInventory.history.push(newHistory);
      promiseList.push(
        API.graphql(
          graphqlOperation(updateInventorieInbound, {
            input: toUpdateInventory,
          }),
        ),
      );
      await Promise.all(promiseList);
      await getData();
      notify(
        `${toCloneInventoryItem.quantityOnHand} ${getUnitName(
          toCloneInventory.inventoryUOM,
        )}
                were placed in ${toCloneInventoryItem.locationFullName}.`,
        "info",
      );
    } catch (err) {
      logError("ProcessWorkOrderCard:handleClone", err, notify);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const validateChecklist = (currentShipment, complete) => {
    debugger;
    const stepName = currentShipment.stepName;
    // No validation for verificationRequired
    if (currentShipment.verificationRequired === "true") return true;
    // No checklist validation for save nor for request approval
    if (stepName === WorkOrderSteps[1] && !complete) return true;

    const workflowSteps = getSteps();
    const currentShipmentStepMap = workflowSteps.reduce((stepsMap, itm) => {
      stepsMap[itm] = itm;
      return stepsMap;
    }, {});
    let uncompletedSteps;
    if (complete && stepName === workflowSteps[workflowSteps.length - 1]) {
      uncompletedSteps = currentShipment.checklist?.filter(
        (itm) =>
          currentShipmentStepMap[itm.stepName] &&
          itm.isRequired &&
          !itm.isCompleted,
      );
    } else {
      uncompletedSteps = currentShipment.checklist?.filter(
        (itm) =>
          currentShipmentStepMap[itm.stepName] &&
          itm.stepName === stepName &&
          itm.isRequired &&
          !itm.isCompleted,
      );
    }

    if (uncompletedSteps?.length > 0) {
      notify(
        `Some checklist items are not completed, please complete the checklist to proceed.`,
        "warning",
      );
      return false;
    }
    return true;
  };

  const handleSealsConfirmationClose = () => {
    setIsSealsConfirmationOpen(false);
    setCompleteFlag(false);
  };

  const handleSealsConfirmationConfirm = () => {
    setIsSealsConfirmationOpen(false);
    handleSave(completeFlag, true);
  };

  /**
   * Validates the placed shipment form before persisting.
   * @param {boolean} complete - True when completing (vs save-only), from the action that triggered save.
   * @param {boolean} [isSealsConfirmed=false] - True after user confirms missing seals in the modal.
   * @param {boolean} isAcceptAction - Required. Must be `(getLeftButtonText() === "Start Work")` from the same user action as save so accept-only checks match the UI. ("Start Work" is the work order's accept action, renamed from the inbound flow's "Accept".)
   */
  const validateForm = (complete, isSealsConfirmed = false, isAcceptAction) => {
    if (typeof isAcceptAction !== "boolean") {
      logError(
        "workOrder:WorkOrderDetailsCard:validateForm:missingIsAcceptAction",
        { received: isAcceptAction },
        notify,
        redirect,
      );
      return false;
    }
    clearForm();
    debugger;
    if (
      shouldBlockAcceptForRailcarVerification({
        isAcceptAction,
        requiresVerification: railcarVerificationState.requiresVerification,
        isRailcarValidated: railcarVerificationState.isRailcarValidated,
      })
    ) {
      setEquipmentVerification({ kind: "railcar", resumeSave: true });
      notify(
        "Validate the arriving railcar # before accepting the work order.",
        "warning",
      );
      return false;
    }

    if (
      shouldBlockAcceptForContainerVerification({
        isAcceptAction,
        containerDrop: currentShipment.containerDrop,
        isContainerNumberValidated,
      })
    ) {
      setEquipmentVerification({ kind: "container", resumeSave: true });
      notify(
        "Validate container # before accepting the work order.",
        "warning",
      );
      return false;
    }

    if (
      currentShipment.account.trackInboundSeals &&
      !currentShipment.sealNumbers?.length &&
      !currentShipment.seals?.items.length &&
      !isSealsConfirmed
    ) {
      setIsSealsConfirmationOpen(true);
      return false;
    }

    if (isAcceptAction && currentShipment.sealNumbers?.length > 0) {
      debugger;
      const acceptSealDialogState = getAcceptSealDialogState({
        isAcceptAction,
        expectedSealNumbers: currentShipment.sealNumbers,
        seals: currentShipment.seals?.items || [],
      });
      if (acceptSealDialogState.shouldOpenSealDialog) {
        setIsSealDialogOpen(true);
        notify(acceptSealDialogState.message, "warning");
        return false;
      }

      let isValidSeals = false;
      isValidSeals =
        currentShipment.seals.items.filter((itm) => !itm.validated).length ===
        0;
      if (!isValidSeals) {
        notify(
          "Invalid seals found, please submit exception for approval.",
          "warning",
        );
        return false;
      }
    }

    let valid = validateChecklist(currentShipment, complete);
    if (currentShipment.state === WorkOrderState.READY) {
      if (currentShipment.verificationRequired === "true") {
        if (!note) {
          valid = false;
          setNoteErrorMsg("A note is required for Approval request.");
        } else {
          setNoteErrorMsg("");
        }
      }
    }

    return valid;
  };

  const clearForm = () => {
    setNoteErrorMsg("");
  };

  const handleSave = async (complete, skipSealValidation = false) => {
    try {
      debugger;
      setCompleteFlag(complete);

      dispatch({ type: "SET_LOADING", payload: true });
      validateShipmentState();
      const { previousStatus, previousState } =
        getPreviousStates(currentShipment);

      // "Start Work" is the work order's accept action (renamed from the inbound
      // flow's "Accept"). Gate the railcar/container/seal verification on it so
      // those checks run when work begins, as they did on Accept.
      const isAcceptAction = getLeftButtonText() === "Start Work";
      if (!validateForm(complete, skipSealValidation, isAcceptAction)) return;

      updateShipmentState(currentShipment, complete);
      addNoteToShipment(currentShipment, note);
      addHistoryToShipment(currentShipment, previousStatus, previousState);

      if (
        complete &&
        currentShipment.state === WorkOrderState.COMPLETED &&
        currentShipment.containerDrop &&
        currentShipment.inventories?.items?.length > 0
      ) {
        for (const inventory of currentShipment.inventories.items) {
          if (!isBulkArea(inventory.areaId)) continue;
          if (getISOtainerInventoryItems(inventory).length === 0) continue;

          const link =
            await ensureBulkMobilePlacementAndAggregateOnInboundComplete({
              shipment: currentShipment,
              inventory,
              account: currentShipment.account,
            });
          if (!link?.aggregateInventoryId?.trim()) {
            notify(
              "Bulk container placement could not be linked to aggregate inventory. Check account and product, then try again.",
              "error",
            );
            throw new Error("bulk_aggregate_link_failed");
          }
          inventory.aggregateInventoryId = link.aggregateInventoryId;
          inventory.status = InventoryStatus.AGGREGATED;
        }
      }

      const body = prepareRequestBody(currentShipment);
      await handleAggregateInventories(body);

      await updateWorkOrderAndInventories(body);

      if (complete) {
        debugger;
        await finalizeShipment(currentShipment);
      }

      handlePostSaveActions(currentShipment, previousState);
    } catch (err) {
      logError(
        "workOrder:WorkOrderDetailsCard:handleSave",
        err,
        notify,
        redirect,
      );
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!pendingAcceptAfterRailcarValidation || !isRailcarValidated) return;

    setPendingAcceptAfterRailcarValidation(false);
    handleSaveRef.current?.(completeFlag);
  }, [pendingAcceptAfterRailcarValidation, isRailcarValidated, completeFlag]);

  useEffect(() => {
    if (!pendingAcceptAfterContainerValidation || !isContainerNumberValidated)
      return;

    setPendingAcceptAfterContainerValidation(false);
    handleSaveRef.current?.(completeFlag);
  }, [
    pendingAcceptAfterContainerValidation,
    isContainerNumberValidated,
    completeFlag,
  ]);

  const validateShipmentState = () => {
    if (!currentShipment.inventories) {
      logError("PlacedShipmentCard:validateShipmentState", currentShipment);
      throw new Error("Invalid state, currentShipment has no inventories.");
    }
  };

  const getPreviousStates = (shipment) => ({
    previousStatus: shipment.status,
    previousState: shipment.state,
  });

  const updateShipmentState = (shipment, complete) => {
    setNextState(shipment, complete);
    if (shipment.state === WorkOrderState.READY) {
      shipment.stepName = WorkOrderSteps[0];
    } else if (shipment.state === WorkOrderState.IN_PROGRESS) {
      shipment.stepName = WorkOrderSteps[1];
    } else {
      shipment.stepName = WorkOrderSteps[2];
    }
  };

  const addNoteToShipment = (shipment, note) => {
    if (!note) return;

    const newNote = {
      date: new Date(),
      by: window.appUser?.fullName,
      type: NoteType.GENERIC,
      text: note,
    };

    shipment.notes = shipment.notes ? [...shipment.notes, newNote] : [newNote];
  };

  const addHistoryToShipment = (shipment, previousStatus, previousState) => {
    if (previousStatus === shipment.status && previousState === shipment.state)
      return;

    const newHistory = {
      type: NoteType.SHIPMENT,
      date: new Date(),
      by: window.appUser?.fullName,
      text: `Work order saved | status: ${shipment.status} ${getLogState(
        shipment.state,
      )}`,
    };

    shipment.history = shipment.history
      ? [...shipment.history, newHistory]
      : [newHistory];
  };

  const prepareRequestBody = (shipment) => {
    const inboundUpdateInput = {
      ...shipment,
      updatedAt: new Date().toISOString(),
    };

    const inventoryUpdateList = shipment.inventories.items.map((inventory) =>
      saveInventory(inventory),
    );

    // Remove unnecessary fields
    [
      "account",
      "area",
      "inventories",
      "photos",
      "reloadYardCommodity",
      "trackCar",
      "zone",
    ].forEach((key) => delete inboundUpdateInput[key]);

    return {
      inboundUpdateInput,
      inventoryUpdateList,
      inventoryItemUpdateList: [],
      locationUpdateList: [],
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    };
  };

  const updateWorkOrderAndInventories = async (body) => {
    // prepareRequestBody / handleAggregateInventories build plain input objects,
    // not promises. Fire the mutations here — Promise.all on the raw inputs would
    // resolve immediately and silently drop every inventory/item update (status
    // → STORED/AGGREGATED, quantityReceived, aggregate quantities, etc.).
    const updates = [];
    if (Array.isArray(body.inventoryUpdateList)) {
      body.inventoryUpdateList.forEach((inventory) =>
        updates.push(performUpdateInventoryMini(inventory)),
      );
    }
    if (Array.isArray(body.inventoryItemUpdateList)) {
      body.inventoryItemUpdateList.forEach((inventoryItem) =>
        updates.push(
          API.graphql(
            graphqlOperation(updateInventoryItemMini, {
              input: sanitizeInventoryItem(inventoryItem),
            }),
          ),
        ),
      );
    }
    if (updates.length) await Promise.all(updates);
    await saveWorkOrder(body.inboundUpdateInput);
  };

  const finalizeShipment = async (shipment) => {
    const workOrder = shipment;

    if (shouldSendPOD(workOrder)) {
      sendPODEmail(workOrder);
    }
    //
    // TODO: Check if this is required or for isTruckToRail before quering the inbound
    // May be needed for cross docking, truck to truck
    //
    await processOutboundShipment(workOrder.inventories.items);
  };

  const shouldSendPOD = (shipment) =>
    !shipment.stockImport &&
    isTrack(shipment.area) &&
    shipment.carrierType === CarrierType.RAIL &&
    shipment.account.podEmail;

  const sendPODEmail = (shipment) => {
    emailPOD(
      shipment,
      shipment.account.podEmail,
      null,
      shipment.account.podPhotos,
      shipment.account.podAttachments,
      notify,
    );

    const podHistoryEntry = {
      text: `POD sent to: ${shipment.account.podEmail}`,
      by: window.appUser?.fullName,
      date: new Date(),
      type: NoteType.GENERIC,
    };
    shipment.history = [...(shipment.history || []), podHistoryEntry];
    // Persist only the appended history; saveWorkOrder re-fetches the rest.
    saveWorkOrder({ id: shipment.id, history: shipment.history });
  };

  const handlePostSaveActions = (shipment, previousState) => {
    dispatch({ type: "CLEAR_NOTE", payload: shipment });

    const workOrderStarted =
      previousState === WorkOrderState.READY &&
      shipment.state === WorkOrderState.IN_PROGRESS;

    if (workOrderStarted) {
      notify("Work order started.", "info");
    } else {
      history?.replace("/processWorkOrders");
    }
  };

  const handleAggregateInventories = async ({
    inventoryUpdateList,
    inventoryItemUpdateList,
  }) => {
    debugger;
    // 1. Ensure we should aggregate at all.
    if (!shouldAggregateInventories(currentShipment, inventoryUpdateList))
      return;

    // 2. Pull the first inventory item (assumed to be the "shipmentInventory").
    const shipmentInventory = inventoryUpdateList[0];
    debugger;
    // 2.5. Aggregation folds this shipment into one clean pool. A shipment
    // carrying damaged stock is left STORED under its own inventory instead:
    // its damage roll-up stays visible in the inventory list and the picker,
    // so a manager can discount, repackage or zero it out. Folding it away
    // would strand the damaged row on an AGGREGATED record no screen reaches.
    const shipmentItems =
      currentShipment.inventories.items.find(
        (itm) => itm.id === shipmentInventory.id,
      )?.inventoryItems?.items || [];
    if (
      shipmentInventory.isDamaged ||
      shipmentItems.some((itm) => itm.isDamaged)
    ) {
      return;
    }

    // 3. Fetch the existing bulk inventory that we'll aggregate into.
    const aggregateInventory = await fetchAggregateInventory(shipmentInventory);
    if (!aggregateInventory) return; // If none found, nothing to aggregate.
    debugger;
    // 4. Update the shipmentInventory and aggregateInventory objects accordingly.
    aggregateShipmentWithInventory(shipmentInventory, aggregateInventory);
    inventoryUpdateList.push(aggregateInventory);
    debugger;
    // 5. Update individual inventoryItems for the aggregated inventory.
    aggregateInventoryItems(
      shipmentInventory,
      aggregateInventory,
      inventoryItemUpdateList,
      currentShipment,
    );
  };

  /**
   * Decide if we need to aggregate inventories at all.
   */
  function shouldAggregateInventories(currentShipment, inventoryUpdateList) {
    return (
      currentShipment.state === WorkOrderState.COMPLETED &&
      inventoryUpdateList.length > 0 &&
      !currentShipment.containerDrop &&
      isBulkArea(inventoryUpdateList[0].areaId)
    );
  }

  /**
   * GraphQL query for fetching the existing "aggregate inventory"
   * to which we add the new shipment.
   */
  async function fetchAggregateInventory(shipmentInventory) {
    try {
      const result = await API.graphql(
        graphqlOperation(inventoryByAreaByProductByStatus, {
          areaId: shipmentInventory.areaId,
          productIdStatus: {
            beginsWith: {
              productId: shipmentInventory.productId,
              status: InventoryStatus.STORED,
            },
          },
          // More than one candidate: a damaged inventory in the same area
          // is skipped below, and with limit 1 it would shadow a clean one.
          limit: 10,
        }),
      );

      // Never aggregate clean stock into an inventory that carries damage --
      // the damaged units would vanish into a clean total. Only clean-to-clean
      // aggregation exists; damaged inventories stay STORED on their own.
      return result.data.inventoryByAreaByProductByStatus.items.find(
        (inv) =>
          !inv.isDamaged &&
          !(inv.inventoryItems?.items || []).some((itm) => itm.isDamaged),
      );
    } catch (error) {
      logError(
        "workOrder:WorkOrderDetailsCard:fetchAggregateInventory",
        error,
        notify,
      );
      return null;
    }
  }

  /**
   * Update the "shipmentInventory" so it knows it’s been aggregated
   * and also update the "aggregateInventory" total quantity.
   */
  function aggregateShipmentWithInventory(
    shipmentInventory,
    aggregateInventory,
  ) {
    // Mark the inbound shipment's inventory as aggregated
    shipmentInventory.status = InventoryStatus.AGGREGATED;
    shipmentInventory.aggregateInventoryId = aggregateInventory.id;

    // Only clean shipments reach this point: handleAggregateInventories skips
    // any shipment carrying damaged stock, and fetchAggregateInventory skips
    // any aggregate carrying it, so the parent total and the item-level
    // roll-up stay in agreement.
    if (!aggregateInventory.quantityOnHand) {
      aggregateInventory.quantityOnHand = 0;
    }
    aggregateInventory.quantityOnHand += shipmentInventory.quantityOnHand;
    aggregateInventory.updatedAt = new Date().toISOString();
  }

  /**
   * For each inventoryItem in the aggregated inventory, update
   * quantityOnHand by adding the inbound shipment's item quantity.
   */
  function aggregateInventoryItems(
    shipmentInventory,
    aggregateInventory,
    inventoryItemUpdateList,
    currentShipment,
  ) {
    debugger;
    // Find the matching shipmentInventory in `currentShipment.inventories`
    const shipmentInvInContext = currentShipment.inventories.items.find(
      (itm) => itm.id === shipmentInventory.id,
    );
    debugger;
    // If none found or no items exist, nothing to do
    if (!shipmentInvInContext || !shipmentInvInContext.inventoryItems?.items) {
      return;
    }
    const shipmentInventoryItems = shipmentInvInContext.inventoryItems.items;
    debugger;
    // Update each aggregateInventoryItem
    // Each shipment item may be folded into at most one aggregate row, and a
    // location can hold several shipment items (repeated placements) as well
    // as several aggregate rows. filter-and-sum takes every matching shipment
    // item at the location; the consumed set stops a second aggregate row at
    // the same location from absorbing the same shipment items again.
    const consumedShipmentItemIds = new Set();
    aggregateInventory.inventoryItems.items.forEach(
      (aggregateInventoryItem) => {
        // Same location is not enough: folding a damaged item's quantity into
        // a clean aggregate row (or the reverse) buries the damaged units in
        // a number nobody can take a claim from. A refused item is simply not
        // rolled up and keeps its own row, the way an item placed in a
        // location the aggregate does not occupy yet already does.
        const matchingShipmentItems = shipmentInventoryItems.filter(
          (itm) =>
            !consumedShipmentItemIds.has(itm.id) &&
            itm.locationId === aggregateInventoryItem.locationId &&
            canMergeInventoryItems(itm, aggregateInventoryItem),
        );

        if (matchingShipmentItems.length > 0) {
          matchingShipmentItems.forEach((itm) =>
            consumedShipmentItemIds.add(itm.id),
          );
          if (!aggregateInventoryItem.quantityOnHand) {
            aggregateInventoryItem.quantityOnHand = 0;
          }
          aggregateInventoryItem.quantityOnHand += matchingShipmentItems.reduce(
            (sum, itm) => sum + (itm.quantityOnHand || 0),
            0,
          );
          aggregateInventoryItem.updatedAt = new Date().toISOString();
          inventoryItemUpdateList.push(aggregateInventoryItem);
        }
      },
    );
  }

  const processOutboundShipment = async (inventories) => {
    debugger;
    // TODO: need to re-write in Lambda function !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    for (let inventory of inventories) {
      if (isTruckToRail(inventory.areaId, currentShipment)) {
        for (let inventoryItem of inventory.inventoryItems.items) {
          if (inventoryItem.outboundShipmentId) {
            await processOutboundInventory(inventory, inventoryItem);
          }
        }
      }
    }
  };

  const processOutboundInventory = async (inventory, inventoryItem) => {
    debugger;
    const result = await doCreateShipmentLineItem(inventory, inventoryItem);
    const promiseList = [];
    const shipmentLineItem = result.data.createShipmentLineItem;
    doCreateShipmentLineItemInventoryItem(
      shipmentLineItem,
      inventoryItem,
      promiseList,
    );
    const response = await API.graphql(
      graphqlOperation(getOutboundShipment, {
        id: inventoryItem.outboundShipmentId,
      }),
    );
    const toUpdateOutbound = response.data.getOutboundShipment;
    promiseList.push(
      syncAutoCreatedShipmentLineItemPoNumber(
        shipmentLineItem.id,
        toUpdateOutbound?.poNumber,
      ),
    );
    toUpdateOutbound.totalUOM = currentShipment.totalUOM;
    toUpdateOutbound.weightUnits = currentShipment.weightUnits;
    toUpdateOutbound.netWeight = calcNetWeight(
      toUpdateOutbound,
      null,
      toUpdateOutbound.weightUnits,
    );
    await updateOutboundQuantity(toUpdateOutbound);
    await Promise.all(promiseList);
  };

  const doCreateShipmentLineItemInventoryItem = async (
    shipmentLineItem,
    inventoryItem,
    promiseList,
  ) => {
    const input = buildShipmentLineItemInventoryItem(
      shipmentLineItem,
      inventoryItem,
    );
    const mutationPromise = API.graphql(
      graphqlOperation(createShipmentLineItemInventoryItem, { input }),
    );

    promiseList.push(mutationPromise);
  };

  const buildShipmentLineItemInventoryItem = (
    shipmentLineItem,
    inventoryItem,
  ) => ({
    groups: shipmentLineItem.groups,
    reloadYardId: shipmentLineItem.reloadYardId,
    outboundShipmentId: shipmentLineItem.outboundShipmentId,
    shipmentLineItemId: shipmentLineItem.id,
    inventoryId: shipmentLineItem.inventoryId,
    inventoryItemId: inventoryItem.id,
    quantityToPick: inventoryItem.quantityOnHand,
    status: shipmentLineItem.status,
  });

  const syncAutoCreatedShipmentLineItemPoNumber = async (
    shipmentLineItemId,
    poNumber,
  ) => {
    if (!shipmentLineItemId || poNumber == null) return;

    try {
      await API.graphql(
        graphqlOperation(updateShipmentLineItem, {
          input: {
            id: shipmentLineItemId,
            poNumber,
          },
        }),
      );
    } catch (err) {
      logError(
        "workOrder:WorkOrderDetailsCard:syncAutoCreatedShipmentLineItemPoNumber",
        err,
        null,
      );
    }
  };

  const doCreateShipmentLineItem = async (inventory, inventoryItem) => {
    const lineItemInput = buildAutoCreatedShipmentLineItem({
      inventory,
      inventoryItem,
      currentUserName: window.appUser?.fullName,
    });
    return API.graphql(
      graphqlOperation(createShipmentLineItem, { input: lineItemInput }),
    );
  };

  const setNextState = (currentShipment, complete) => {
    debugger;
    if (currentShipment.state === WorkOrderState.READY) {
      if (currentShipment.verificationRequired === "true") {
        currentShipment.state = WorkOrderState.PENDING_REVIEW;
      } else {
        currentShipment.state = WorkOrderState.IN_PROGRESS;
        currentShipment.startDateTime = new Date().toISOString();
      }
      if (currentShipment.inventories) {
        currentShipment.inventories.items.forEach((inventory) => {
          inventory.status = InventoryStatus.ARRIVED;
        });
      }
    } else if (currentShipment.state === WorkOrderState.PENDING_REVIEW) {
      // PENDING_REVIEW collapses two legacy inbound states (manager-approval
      // pending vs. secondary-review pending). Disambiguate by `secondaryReview`
      // + `complete`: if the user is completing a secondary review, mark the
      // work order COMPLETED; otherwise treat as approval released and resume
      // processing.
      if (currentShipment.verificationRequired !== "true") {
        if (complete && currentShipment.secondaryReview) {
          currentShipment.state = WorkOrderState.COMPLETED;
          currentShipment.processedBy = window.appUser.fullName;
          currentShipment.endDateTime = new Date().toISOString();
        } else {
          currentShipment.state = WorkOrderState.IN_PROGRESS;
        }
      }
    } else if (currentShipment.state === WorkOrderState.IN_PROGRESS) {
      if (currentShipment.verificationRequired === "true") {
        currentShipment.state = WorkOrderState.PENDING_REVIEW;
      } else if (complete) {
        currentShipment.processedBy = window.appUser.fullName;

        if (currentShipment.secondaryReview) {
          currentShipment.state = WorkOrderState.PENDING_REVIEW;
          currentShipment.endDateTime = new Date().toISOString();
          if (currentShipment.inventories) {
            currentShipment.inventories.items.forEach(
              (itm) => (itm.status = InventoryStatus.REVIEW),
            );
          }
        } else {
          currentShipment.state = WorkOrderState.COMPLETED;
          currentShipment.endDateTime = new Date().toISOString();
          if (currentShipment.inventories) {
            currentShipment.inventories.items.forEach((inventory) => {
              if (
                isBulkArea(inventory.areaId) &&
                !inventory.containerNumber?.trim()
              ) {
                inventory.status = InventoryStatus.AGGREGATED;
              } else {
                inventory.status = InventoryStatus.STORED;
              }
            });
          }
        }
      }
    }
  };

  const getLeftButtonText = () => {
    let text = "Save";
    if (currentShipment?.verificationRequired === "true") {
      text = "Request Approval";
    } else if (currentShipment?.state === WorkOrderState.READY) {
      text = "Start Work";
    }
    return text;
  };

  const getRightButtonText = () => {
    if (
      currentShipment?.state === WorkOrderState.IN_PROGRESS &&
      currentShipment?.secondaryReview
    )
      return "Mark Stored";
    return "Mark Complete";
  };

  const toggleChecked = (value) => {
    currentShipment.verificationRequired = value;
    setNoteErrorMsg("");
    dispatch({ type: "SET_CURRENT_SHIPPMENT", payload: currentShipment });
  };

  const setShipmentNote = (value) => {
    if (value?.length > 0) setNoteErrorMsg("");

    dispatch({
      type: "SET_SHIPPMENT_NOTE",
      payload: { id: currentShipment.id, value },
    });
  };

  const isVerificationDisabled = () => {
    if (!currentShipment?.id) return false;
    if (currentShipment.inventories) {
      const itemsNeedVerification = currentShipment.inventories.items?.some(
        (itm) => itm.verificationRequired === "true",
      );
      if (itemsNeedVerification) return true;
    }
    return false;
  };

  const isCompleteDisabled = () => {
    if (loading) return true;
    let disabled = false;

    if (currentShipment.verificationRequired === "true") disabled = true;
    else if (
      !currentShipment.inventories ||
      currentShipment.inventories?.items?.length === 0
    )
      disabled = true;
    else {
      disabled = currentShipment.inventories.items.some(
        (item) =>
          item.status !== InventoryStatus.UNLOADED &&
          item.status !== InventoryStatus.STORED,
      );
    }
    return disabled;
  };

  const isLeftButtonDisabled = () => loading;

  const getShipmentIcon = () => {
    if (currentShipment?.carrierType === CarrierType.RAIL)
      return <TrainIcon fontSize="large" />;
    return <TruckIcon fontSize="large" />;
  };

  if (currentShipment) {
    currentShipment.verificationRequired =
      (currentShipment.verificationRequired === "true" ||
        currentShipment.inventories?.items.some(
          (item) => item.verificationRequired === "true",
        )) + ""; // adding "" forces string type
  }

  const handleSpeedDialClose = () => setIsSpeedDialOpen(false);

  const handleSpeedDialOpen = (evt, reason) => {
    if (reason === "mouseEnter" || reason === "focus") return;
    setIsSpeedDialOpen(true);
  };

  const getSealConfirmMessage = () => {
    const action = isCompleteDisabled() ? "Start Work" : "Complete";
    return `No seals have been scanned for this work order. ${action} the work order without seals?`;
  };

  if (invalidIdError) return <ErrorCard error={invalidIdError} />;

  if (!currentShipment?.id) {
    if (loading) {
      return (
        <CrewCard title="" subheader="" showDivider={false}>
          <CardContent>
            <Loading className={classes.backdrop} loading />
          </CardContent>
        </CrewCard>
      );
    }
    return (
      <ErrorCard error="Unable to load work order. Go back and try again." />
    );
  }

  const onProceedWithException = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      currentShipment.hasException = true;
      currentShipment.verificationRequired = "true";
      // Pass only the fields that exist on WorkOrder; saveWorkOrder re-fetches
      // the full canonical detail after the mutation.
      await saveWorkOrder({
        id: currentShipment.id,
        hasException: true,
        verificationRequired: "true",
      });
      handleEquipmentVerificationClose();
    } catch (error) {
      logError("ProcessWorkOrderCard:onProceedWithException", error, notify);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  // Checklist lives on InboundShipment (the WorkOrder schema has no `checklist`
  // field). Persist edits to the linked inbound, then re-load the work order
  // so the joined inputInventory.inboundShipment.checklist reflects the new
  // state in `currentShipment`.
  const saveChecklistToInbound = async (model) => {
    const inboundId =
      currentShipment?.inboundShipmentId ||
      currentShipment?.inputInventory?.inboundShipmentId;
    if (!inboundId) {
      throw new Error(
        "ChecklistDialog: current work order has no linked inboundShipmentId",
      );
    }
    await performUpdateInboundMini({
      id: inboundId,
      checklist: model.checklist || [],
    });
    await getData({ showSpinner: false });
  };

  return (
    <CrewCard
      title={accountNameRenderer(currentShipment)}
      icon={getShipmentIcon()}
      subheader=""
      action={
        <IconButton edge="start" onClick={() => backHandler(true)}>
          <HighlightOffIcon fontSize="large" />
        </IconButton>
      }
      showDivider={false}
    >
      <CardContent>
        <Loading className={classes.backdrop} loading={loading} />
        <Grid container spacing={1}>
          <InboundStepperProgress
            getActiveStep={getActiveStep}
            getSteps={getSteps}
            isStepFailed={isStepFailed}
            isStepCompleted={isStepCompleted}
          />
          <CardInfo
            lotNumber={currentShipment.lotNumber}
            poNumber={currentShipment.poNumber}
            customFields={currentShipment.customFields}
            account={currentShipment.account}
            instructions={currentShipment.instructions}
          />
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  color={
                    currentShipment.verificationRequired === "true"
                      ? "secondary"
                      : "default"
                  }
                  checked={currentShipment.verificationRequired === "true"}
                  disabled={isVerificationDisabled()}
                  onChange={() =>
                    toggleChecked(
                      currentShipment.verificationRequired === "true"
                        ? "false"
                        : "true",
                    )
                  }
                />
              }
              label="Request approval"
            />

            {isExceptionDialogOpen && (
              <ExceptionDialog
                classes={classes}
                isExceptionDialogOpen={isExceptionDialogOpen}
                setIsExceptionDialogOpen={setIsExceptionDialogOpen}
                saveWorkOrder={saveWorkOrder}
              ></ExceptionDialog>
            )}
            {isSealDialogOpen && (
              <SealDialog
                setIsSealDialogOpen={setIsSealDialogOpen}
              ></SealDialog>
            )}
            {equipmentVerification?.kind === "railcar" && (
              <RailCarNumberVerificationDialog
                open
                expectedRailCarNumber={
                  railcarVerificationState.expectedRailCarNumber
                }
                close={handleEquipmentVerificationClose}
                scanEnabled={false}
                onValidationSuccess={handleEquipmentVerificationSuccess}
              />
            )}
            {equipmentVerification?.kind === "container" && (
              <ContainerNumberDialog
                open
                containerNumber={currentShipment.containerNumber}
                close={handleEquipmentVerificationClose}
                onProceed={onProceedWithException}
                scanEnabled={false}
                onValidationSuccess={handleEquipmentVerificationSuccess}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={6}>
            {isChecklistDialogOpen && (
              <ChecklistDialog
                model={currentShipment}
                isOpen={isChecklistDialogOpen}
                setOpen={setIsChecklistDialogOpen}
                saveModel={saveChecklistToInbound}
                title="Work Order Checklist"
                stepName={currentShipment.stepName}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="notes"
              id="outlined-multiline-static"
              label="Notes"
              multiline
              rows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={note}
              error={noteErrorMsg ? true : false}
              onChange={(evt) => setShipmentNote(evt.target.value)}
            />
            {noteErrorMsg && (
              <div className={classes.photoErrorSection}>
                <Typography variant="caption" display="block" color="error">
                  {noteErrorMsg}
                </Typography>
              </div>
            )}
          </Grid>
          {currentShipment.inventories?.items.length > 0 ? (
            <InboundInventoryList
              classes={classes}
              currentShipment={currentShipment}
              history={history}
              match={match}
              handleClone={handleClone}
              cloneableInventoriesByProductName={
                cloneableInventoriesByProductName
              }
              cloneableInventoriesByProductId={cloneableInventoriesByProductId}
              disabled={currentShipment.state === WorkOrderState.READY}
            />
          ) : (
            <Typography className={classes.notFound}>
              No work-order inventories found.
            </Typography>
          )}
        </Grid>
        <SpeedDialList
          classes={classes}
          handleSpeedDialClose={handleSpeedDialClose}
          handleSpeedDialOpen={handleSpeedDialOpen}
          isSpeedDialOpen={isSpeedDialOpen}
          actions={actions}
        />
        <SealsConfirmationDialog
          msg={getSealConfirmMessage()}
          open={isSealsConfirmationOpen}
          onClose={handleSealsConfirmationClose}
          onConfirm={handleSealsConfirmationConfirm}
        />
      </CardContent>
      <CardActions className={classes.actions}>
        <Button
          type="submit"
          size="large"
          onClick={() => handleSave(false)}
          disabled={isLeftButtonDisabled()}
        >
          {getLeftButtonText()}
        </Button>
        <Button
          size="large"
          onClick={() => handleSave(true)}
          disabled={isCompleteDisabled()}
          color="primary"
        >
          {getRightButtonText()}
        </Button>
      </CardActions>
    </CrewCard>
  );
};

export const Loading = ({ className, loading }) => (
  <Backdrop className={className} open={loading}>
    <CircularProgress color="inherit" />
  </Backdrop>
);

const CardInfo = ({
  lotNumber,
  poNumber,
  customFields,
  account,
  instructions,
}) => (
  <>
    <Grid item sm={6} xs={12}>
      <Box mt={1} alignItems="flex-end">
        <Typography>Lot Number: {lotNumber}</Typography>
      </Box>
    </Grid>
    <Grid item sm={6} xs={12}>
      <Box mt={1} alignItems="flex-end">
        <Typography>PO Number: {poNumber}</Typography>
      </Box>
    </Grid>
    {customFields && (
      <Grid item xs={12}>
        <CustomFieldsMobile
          recordFields={customFields}
          accountFields={account?.inboundCustomFields}
          width={6}
        />
      </Grid>
    )}
    {instructions && (
      <Grid item xs={12}>
        <Typography>Instructions: {instructions}</Typography>
      </Grid>
    )}
  </>
);
